/**
 * Structured run timeline.
 *
 * Deliberately coarse: events are derived server-side from git and phase
 * markers (see `AgentRunEvent.type`), not the agent's own internal steps.
 * This renders exactly what each event says — "agent-start", "edited
 * poam.yaml" — and never invents intermediate narrative the event stream
 * does not carry. Raw prose lives in the GitLab job trace, linked from the
 * run detail page, not mirrored here.
 */

import type { ReactElement } from 'react';
import type { AgentRunEvent, RunEventType } from '../types/agentRun.types';

interface RunTimelineProps {
  events: AgentRunEvent[];
}

function stringField(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' ? value : undefined;
}

const DESCRIBE_BY_TYPE: Record<RunEventType, (payload: Record<string, unknown>) => string> = {
  phase: (payload) => stringField(payload, 'name') ?? 'phase',
  file_change: (payload) => `edited ${stringField(payload, 'path') ?? 'a file'}`,
  command: (payload) => stringField(payload, 'command') ?? 'ran a command',
  finding: (payload) => stringField(payload, 'title') ?? 'finding',
  error: (payload) => stringField(payload, 'message') ?? 'error',
};

function describeEvent(event: AgentRunEvent): string {
  return DESCRIBE_BY_TYPE[event.type](event.payload);
}

export function RunTimeline({ events }: RunTimelineProps): ReactElement {
  if (events.length === 0) {
    return <p className="run-timeline__empty">No steps recorded yet.</p>;
  }

  const sorted = [...events].sort((a, b) => a.seq - b.seq);

  return (
    <ol className="run-timeline">
      {sorted.map((event) => (
        <li key={event.seq} className="run-timeline__row">
          <span className="run-timeline__time">
            {new Date(event.ts).toLocaleTimeString()}
          </span>
          <span className="run-timeline__type">{event.type}</span>
          <span className="run-timeline__desc">{describeEvent(event)}</span>
        </li>
      ))}
    </ol>
  );
}
